import type { Entry } from "./types";

export function formatNumber(value: string | number): string {
  return String(value).padStart(3, "0");
}

export function formatMoney(value: number, currency = "MMK"): string {
  const amount = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(value));
  return `${amount} ${currency}`;
}

export function formatDate(value: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Yangon" }).format(date);
}

export function localDateInput(): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Yangon", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export type ParsedLine = { line: number; number: string; amount: number };
export type ParseResult = { entries: Entry[]; invalid: Array<{ line: number; value: string }> };

type InheritedBet =
  | { type: "straight"; amount: number }
  | { type: "R"; amount: number; exactAmount: number };

function uniquePermutations(number: string): string[] {
  const [a, b, c] = number.split("");
  return Array.from(new Set([`${a}${b}${c}`, `${a}${c}${b}`, `${b}${a}${c}`, `${b}${c}${a}`, `${c}${a}${b}`, `${c}${b}${a}`]));
}

function parseAmount(value: string): number | null {
  if (!/^\d+(?:,\d{3})*$/.test(value)) return null;
  const amount = Number(value.replaceAll(",", ""));
  return Number.isSafeInteger(amount) && amount > 0 ? amount : null;
}

export function parseBulkEntries(text: string): ParseResult {
  const entries = new Map<string, number>();
  const invalid: Array<{ line: number; value: string }> = [];
  let inheritedBet: InheritedBet | null = null;
  const addEntry = (number: string, amount: number) => {
    entries.set(number, (entries.get(number) ?? 0) + amount);
  };
  const addR = (number: string, exactAmount: number, permutationAmount: number) => {
    for (const permutation of uniquePermutations(number)) {
      addEntry(permutation, permutation === number ? exactAmount : permutationAmount);
    }
  };
  text.split(/\r?\n/).forEach((raw, index) => {
    const value = raw.trim();
    if (!value) return;
    const triMatch = value.match(/^T(?:\s+|=|,)([0-9][0-9,]*)$/i);
    if (triMatch) {
      const amount = parseAmount(triMatch[1]);
      if (amount === null) {
        invalid.push({ line: index + 1, value });
        return;
      }
      for (let digit = 0; digit <= 9; digit += 1) {
        const number = String(digit).repeat(3);
        addEntry(number, amount);
      }
      inheritedBet = null;
      return;
    }

    const mixedRMatch = value.match(/^(\d{3})(?:\s+|=|,|\.)([0-9][0-9,]*)[\s,.]*R(?:\s+|=|,|\.)([0-9][0-9,]*)$/i);
    if (mixedRMatch) {
      const exactAmount = parseAmount(mixedRMatch[2]);
      const permutationAmount = parseAmount(mixedRMatch[3]);
      if (exactAmount === null || permutationAmount === null) {
        invalid.push({ line: index + 1, value });
        return;
      }
      inheritedBet = { type: "R", exactAmount, amount: permutationAmount };
      addR(mixedRMatch[1], exactAmount, permutationAmount);
      return;
    }

    const rMatch = value.match(/^(\d{3})[\s,.]*R(?:\s+|=|,)([0-9][0-9,]*)$/i);
    if (rMatch) {
      const amount = parseAmount(rMatch[2]);
      if (amount === null) {
        invalid.push({ line: index + 1, value });
        return;
      }
      inheritedBet = { type: "R", exactAmount: amount, amount };
      addR(rMatch[1], amount, amount);
      return;
    }

    const straightMatch = value.match(/^(\d{3})(?:\s+|=|,|\.)([0-9][0-9,]*)$/);
    if (straightMatch) {
      const amount = parseAmount(straightMatch[2]);
      if (amount === null) {
        invalid.push({ line: index + 1, value });
        return;
      }
      inheritedBet = { type: "straight", amount };
      addEntry(straightMatch[1], amount);
      return;
    }

    const numberMatch = value.match(/^(\d{3})$/);
    if (numberMatch && inheritedBet) {
      if (inheritedBet.type === "R") addR(numberMatch[1], inheritedBet.exactAmount, inheritedBet.amount);
      else addEntry(numberMatch[1], inheritedBet.amount);
      return;
    }

    if (!numberMatch || !inheritedBet) {
      invalid.push({ line: index + 1, value });
      return;
    }
  });
  return { entries: Array.from(entries, ([number, amount]) => ({ number, amount })).sort((a, b) => a.number.localeCompare(b.number)), invalid };
}
